import React, { useState, useEffect } from 'react';
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Button, Box,
    Typography, CircularProgress, Alert, Snackbar, Dialog, DialogActions, DialogContent, DialogTitle,
    TextField, Grid, Chip, InputAdornment, IconButton
} from '@mui/material';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';

const Billing = ({ api, isAuthenticated }) => {
    const stripe = useStripe();
    const elements = useElements();

    const [bills, setBills] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [paymentProcessing, setPaymentProcessing] = useState(false);
    const [selectedBill, setSelectedBill] = useState(null);
    const [paymentDetails, setPaymentDetails] = useState({
        name: '',
        email: '',
        discountCode: ''
    });
    const [trialExpired, setTrialExpired] = useState(false);

    // Discount validation states
    const [validatingDiscount, setValidatingDiscount] = useState(false);
    const [discountValid, setDiscountValid] = useState(null);
    const [discountMessage, setDiscountMessage] = useState('');
    const [discountedAmount, setDiscountedAmount] = useState(null);
    const [originalAmount, setOriginalAmount] = useState(null);
    const [discountApplied, setDiscountApplied] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const showSnackbar = (message, severity = 'success') =>
        setSnackbar({ open: true, message, severity });

    useEffect(() => {
        if (isAuthenticated) {
            fetchBills();
            fetchStoreDetails();
        }
    }, [isAuthenticated]);

    const fetchStoreDetails = () => {
        api.get('/api/store')
            .then((res) => {
                const details = res.data.details || {};
                // Pre-fill payment dialog name/email from billing details
                setPaymentDetails((prev) => ({
                    ...prev,
                    name: details.billing_name || '',
                    email: details.billing_email || '',
                }));
                // Check if trial has expired
                if (details.trial && details.billing_date && new Date(details.billing_date) < new Date()) {
                    setTrialExpired(true);
                }
            })
            .catch(() => {});
    };

    const fetchBills = () => {
        setLoading(true);
        api.get('/api/billing')
            .then(response => {
                setBills(response.data.bills);
                setLoading(false);
            })
            .catch(() => {
                setError('Failed to fetch billing records');
                setLoading(false);
            });
    };

    // Validate discount code with the server
    const validateDiscountCode = async () => {
        if (!paymentDetails.discountCode.trim()) {
            setDiscountValid(false);
            setDiscountMessage('Please enter a discount code');
            return;
        }

        if (!selectedBill) return;

        setValidatingDiscount(true);
        setDiscountValid(null);
        setDiscountMessage('');

        try {
            const response = await api.post('/api/discount/validate', {
                code: paymentDetails.discountCode,
                amount: selectedBill.amount,
                billId: selectedBill.bill_id
            });

            if (response.data.valid) {
                setDiscountValid(true);
                setDiscountApplied(true);
                setDiscountedAmount(parseFloat(response.data.finalAmount));
                setOriginalAmount(selectedBill.amount);
                setDiscountMessage(response.data.message || 'Discount applied successfully!');
            } else {
                setDiscountValid(false);
                setDiscountApplied(false);
                setDiscountedAmount(null);
                setDiscountMessage(response.data.message || 'Invalid discount code');
            }
        } catch (error) {
            console.error('Error validating discount:', error);
            setDiscountValid(false);
            setDiscountApplied(false);
            setDiscountedAmount(null);
            setDiscountMessage(error.response?.data?.error || 'Failed to validate discount code');
        } finally {
            setValidatingDiscount(false);
        }
    };

    const handlePay = async () => {
        if (!validatePaymentDetails()) return;
        setPaymentProcessing(true);

        const isPromoWaived = discountApplied && discountedAmount === 0;
        let paymentMethodId = null;

        if (!isPromoWaived) {
            if (!stripe || !elements) {
                setError('Stripe is not initialized.');
                setPaymentProcessing(false);
                return;
            }

            const cardElement = elements.getElement(CardElement);
            const { paymentMethod, error: stripeError } = await stripe.createPaymentMethod({
                type: 'card',
                card: cardElement,
                billing_details: {
                    name: paymentDetails.name,
                    email: paymentDetails.email
                }
            });

            if (stripeError) {
                setError(stripeError.message);
                setPaymentProcessing(false);
                return;
            }
            paymentMethodId = paymentMethod.id;
        }

        // Send the PaymentMethod ID and discount info to the backend
        api.post(`/api/billing/${selectedBill.bill_id}/pay`, {
            paymentDetails: {
                name: paymentDetails.name,
                email: paymentDetails.email,
                paymentMethodId,
                discountCode: discountApplied ? paymentDetails.discountCode : null
            }
        })
            .then((response) => {
                setError(null);
                fetchBills();
                setSelectedBill(null);

                // Show success message with discount applied if any
                if (response.data.discountApplied) {
                    showSnackbar(`Payment successful! Discount applied: $${response.data.discountAmount} off. Final amount: $${response.data.finalAmount}`);
                } else {
                    showSnackbar('Payment successful!');
                }

                // Reset discount states
                resetDiscountStates();
            })
            .catch((error) => {
                setError(error.response?.data?.message || 'Payment failed');
            })
            .finally(() => {
                setPaymentProcessing(false);
            });
    };

    const validatePaymentDetails = () => {
        const { name, email } = paymentDetails;
        if (!name || !email) {
            setError('Name and email are required.');
            return false;
        }
        return true;
    };

    const resetDiscountStates = () => {
        setDiscountValid(null);
        setDiscountMessage('');
        setDiscountedAmount(null);
        setOriginalAmount(null);
        setDiscountApplied(false);
    };

    const handleDialogClose = () => {
        setSelectedBill(null);
        setPaymentDetails((prev) => ({ ...prev, discountCode: '' }));
        resetDiscountStates();
    };

    const handleDiscountCodeChange = (e) => {
        setPaymentDetails({ ...paymentDetails, discountCode: e.target.value });
        // Reset validation when code changes
        setDiscountValid(null);
        setDiscountMessage('');
        setDiscountedAmount(null);
        setDiscountApplied(false);
    };

    return (
        <Box sx={{ p: 2 }}>
            <Typography variant="h4" gutterBottom sx={{ fontSize: '1.5rem' }}>Billing</Typography>
            {trialExpired && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    Your 7-day free trial has ended. Pay your invoice below to restore full access.
                </Alert>
            )}
            {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
                    <CircularProgress />
                </Box>
            ) : (
                <TableContainer component={Paper} sx={{ mt: 2 }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Bill ID</TableCell>
                                <TableCell>Transaction ID</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Due Date</TableCell>
                                <TableCell>Amount</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {bills.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center">
                                        <Alert severity="info">No bills found.</Alert>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                bills.map(bill => (
                                    <TableRow key={bill.bill_id}>
                                        <TableCell>{bill.bill_id}</TableCell>
                                        <TableCell>{bill.trans_id}</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={bill.status}
                                                color={bill.status === 'Pending' ? 'warning' : 'success'}
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell>{bill.due_date}</TableCell>
                                        <TableCell>${bill.amount.toFixed(2)}</TableCell>
                                        <TableCell>
                                            {bill.status === 'Pending' && (
                                                <Button
                                                    variant="contained"
                                                    color="primary"
                                                    disabled={paymentProcessing}
                                                    onClick={() => {
                                                        setSelectedBill(bill);
                                                        // Keep pre-filled name/email from store details; reset discount code only
                                                        setPaymentDetails((prev) => ({ ...prev, discountCode: '' }));
                                                        resetDiscountStates();
                                                    }}
                                                >
                                                    Pay
                                                </Button>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Payment Dialog */}
            <Dialog
                open={Boolean(selectedBill)}
                onClose={handleDialogClose}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Enter Payment Details</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Name"
                                variant="outlined"
                                value={paymentDetails.name}
                                onChange={(e) => setPaymentDetails({ ...paymentDetails, name: e.target.value })}
                                required
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Email"
                                variant="outlined"
                                type="email"
                                value={paymentDetails.email}
                                onChange={(e) => setPaymentDetails({ ...paymentDetails, email: e.target.value })}
                                required
                            />
                        </Grid>

                        {/* Discount Code Section */}
                        <Grid item xs={12}>
                            <Typography variant="subtitle1" gutterBottom>
                                Have a discount code?
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                                <TextField
                                    fullWidth
                                    label="Discount Code"
                                    variant="outlined"
                                    value={paymentDetails.discountCode}
                                    onChange={handleDiscountCodeChange}
                                    disabled={validatingDiscount || discountApplied}
                                    InputProps={{
                                        endAdornment: discountValid === true ? (
                                            <InputAdornment position="end">
                                                <CheckCircleIcon color="success" />
                                            </InputAdornment>
                                        ) : discountValid === false ? (
                                            <InputAdornment position="end">
                                                <ErrorIcon color="error" />
                                            </InputAdornment>
                                        ) : null
                                    }}
                                />
                                <Button
                                    variant="outlined"
                                    onClick={validateDiscountCode}
                                    disabled={!paymentDetails.discountCode.trim() || validatingDiscount || discountApplied}
                                    sx={{ minWidth: '100px' }}
                                >
                                    {validatingDiscount ? <CircularProgress size={24} /> : 'Apply'}
                                </Button>
                            </Box>

                            {/* Discount validation message */}
                            {discountMessage && (
                                <Alert
                                    severity={discountValid ? 'success' : 'error'}
                                    sx={{ mt: 1 }}
                                    onClose={() => setDiscountMessage('')}
                                >
                                    {discountMessage}
                                </Alert>
                            )}

                            {/* Amount summary with discount */}
                            {selectedBill && (
                                <Box sx={{
                                    mt: 2,
                                    p: 2,
                                    bgcolor: discountApplied ? '#e8f5e8' : '#f5f5f5',
                                    borderRadius: 1
                                }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Original Amount: ${selectedBill.amount.toFixed(2)}
                                    </Typography>

                                    {discountApplied && discountedAmount !== null && (
                                        <>
                                            <Typography variant="body2" color="success.main" sx={{ fontWeight: 'bold' }}>
                                                Discount: -${(selectedBill.amount - discountedAmount).toFixed(2)}
                                            </Typography>
                                            <Typography variant="h6" sx={{ mt: 1, color: 'success.main' }}>
                                                Final Amount: ${discountedAmount.toFixed(2)}
                                            </Typography>
                                        </>
                                    )}

                                    {!discountApplied && (
                                        <Typography variant="h6" sx={{ mt: 1 }}>
                                            Amount to Pay: ${selectedBill.amount.toFixed(2)}
                                        </Typography>
                                    )}
                                </Box>
                            )}
                        </Grid>

                        {/* Card Element — hidden when promo code fully waives the bill */}
                        {!(discountApplied && discountedAmount === 0) && (
                        <Grid item xs={12}>
                            <Box sx={{
                                mt: 2,
                                p: 2,
                                border: '1px solid #e0e0e0',
                                borderRadius: 1,
                                bgcolor: '#fafafa'
                            }}>
                                <CardElement options={{
                                    style: {
                                        base: {
                                            fontSize: '16px',
                                            color: '#424770',
                                            '::placeholder': {
                                                color: '#aab7c4',
                                            },
                                        }
                                    }
                                }} />
                            </Box>
                        </Grid>
                        )}
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={handleDialogClose} color="secondary">
                        Cancel
                    </Button>
                    <Button
                        onClick={handlePay}
                        color="primary"
                        variant="contained"
                        disabled={paymentProcessing || !paymentDetails.name || !paymentDetails.email}
                        sx={{ minWidth: '150px' }}
                    >
                        {paymentProcessing ? (
                            <CircularProgress size={24} />
                        ) : (
                            `Pay $${(discountApplied && discountedAmount !== null ? discountedAmount : selectedBill?.amount || 0).toFixed(2)}`
                        )}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={5000}
                onClose={() => setSnackbar(s => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default Billing;